/**
 * Altvia Interaction Text Highlighter
 * Works at the text node level, not component level
 * Follows the same pattern as DocxViewer and PDF highlighters
 */

/**
 * Main function to handle Altvia highlighting based on fileViewerData
 * This follows the same pattern as other document viewers
 */
export function handleAltviaHighlighting(fileViewerData: any) {
  // Find all content containers in the Altvia interaction
  const containers = document.querySelectorAll(
    ".altvia-content, .altvia-reference, .altvia-description"
  ) as NodeListOf<HTMLElement>;
  if (!containers.length) {
    return;
  }

  // Clear existing highlights first
  clearAllHighlights();

  // A table cell highlights its value or nothing (#12090); a cell chip that
  // carries no value of its own has nothing to highlight, and its passage
  // is the whole source table.
  const cellWithoutValue =
    fileViewerData.exactOnly &&
    !`${fileViewerData.originalNumValue ?? ""}`.trim();

  // Handle context highlighting
  if (fileViewerData.highlightContext && !cellWithoutValue) {
    let didHighlight = false;

    // Try to highlight in each container
    for (const container of containers) {
      const highlightResult = highlightTextInContainer(
        container,
        fileViewerData.highlightContext,
        fileViewerData.originalNumValue || ""
      );

      if (highlightResult) {
        didHighlight = true;
        break; // Found a match, no need to continue
      }
    }

    // A table cell wants the value where the note prints it, or nothing
    // (#12090): no fuzzy fallback for it.
    if (!didHighlight && !fileViewerData.exactOnly) {
      // Use fuzzy highlighting as fallback
      applyFuzzyHighlighting(containers, fileViewerData);
    }
  }
}

/**
 * Highlights text within a specific container at the text node level
 */
function highlightTextInContainer(
  container: HTMLElement,
  context: string,
  snippet?: string
): boolean {
  const textNodes = getTextNodes(container);
  if (!textNodes.length) return false;

  if (snippet && snippet.trim()) {
    // Try to highlight the specific snippet first
    return highlightSnippetInTextNodes(textNodes, snippet);
  } else {
    // Try to highlight the context
    return highlightContextInTextNodes(textNodes, context);
  }
}

/**
 * Highlights a specific snippet (like a number) within text nodes
 */
function highlightSnippetInTextNodes(
  textNodes: Node[],
  snippet: string
): boolean {
  const snippetLower = snippet.toLowerCase();

  for (const node of textNodes) {
    if (node.nodeType !== Node.TEXT_NODE) continue;

    const content = node.textContent || "";
    const idx = content.toLowerCase().indexOf(snippetLower);

    if (idx !== -1) {
      // Found the snippet, highlight it
      const mark = createHighlightMark(
        content.substring(idx, idx + snippet.length)
      );

      // Split the text node
      const beforeText = content.substring(0, idx);
      const afterText = content.substring(idx + snippet.length);

      const parent = node.parentNode;
      if (!parent) continue;

      // Replace the text node with our highlighted version
      if (beforeText) {
        parent.insertBefore(document.createTextNode(beforeText), node);
      }
      parent.insertBefore(mark, node);
      if (afterText) {
        parent.insertBefore(document.createTextNode(afterText), node);
      }

      parent.removeChild(node);

      // Scroll to the highlight
      mark.scrollIntoView({ behavior: "smooth", block: "center" });
      return true;
    }
  }

  return false;
}

/**
 * Highlights context text within text nodes
 */
function highlightContextInTextNodes(
  textNodes: Node[],
  context: string
): boolean {
  const contextLower = context.toLowerCase();

  // First try exact matches
  for (const node of textNodes) {
    if (node.nodeType !== Node.TEXT_NODE) continue;

    const content = node.textContent || "";
    const idx = content.toLowerCase().indexOf(contextLower);

    if (idx !== -1) {
      // Found the context, highlight it
      const mark = createHighlightMark(
        content.substring(idx, idx + context.length)
      );

      // Split the text node
      const beforeText = content.substring(0, idx);
      const afterText = content.substring(idx + context.length);

      const parent = node.parentNode;
      if (!parent) continue;

      // Replace the text node with our highlighted version
      if (beforeText) {
        parent.insertBefore(document.createTextNode(beforeText), node);
      }
      parent.insertBefore(mark, node);
      if (afterText) {
        parent.insertBefore(document.createTextNode(afterText), node);
      }

      parent.removeChild(node);

      // Scroll to the highlight
      mark.scrollIntoView({ behavior: "smooth", block: "center" });
      return true;
    }
  }

  return false;
}

/**
 * Creates a highlight mark element
 */
function createHighlightMark(text: string): HTMLElement {
  const mark = document.createElement("mark");
  mark.classList.add("fp-dark-highlight", "altvia-highlight");
  mark.textContent = text;
  return mark;
}

/**
 * Gets all text nodes within a container
 */
function getTextNodes(container: HTMLElement): Node[] {
  const walker = document.createTreeWalker(
    container,
    NodeFilter.SHOW_TEXT,
    null
  );

  const textNodes: Node[] = [];
  let node;
  while ((node = walker.nextNode())) {
    if (node.textContent && node.textContent.trim()) {
      textNodes.push(node);
    }
  }

  return textNodes;
}

/**
 * Applies fuzzy highlighting when exact matches aren't found
 */
function applyFuzzyHighlighting(
  containers: NodeListOf<HTMLElement>,
  fileViewerData: any
) {
  // This is a simplified version - in practice, you might want to implement
  // more sophisticated fuzzy matching like the PDF viewer does

  for (const container of containers) {
    const textNodes = getTextNodes(container);

    // Highlight the first few text nodes as a fallback
    for (let i = 0; i < Math.min(3, textNodes.length); i++) {
      const node = textNodes[i];
      if (node.nodeType === Node.TEXT_NODE) {
        const content = node.textContent || "";
        if (content.trim()) {
          // Highlight the first meaningful text
          const words = content.trim().split(/\s+/);
          if (words.length > 0) {
            const firstWord = words[0];
            const mark = createHighlightMark(firstWord);

            const parent = node.parentNode;
            if (!parent) continue;

            const afterText = content.substring(firstWord.length);

            if (afterText) {
              parent.insertBefore(document.createTextNode(afterText), node);
            }
            parent.insertBefore(mark, node);
            parent.removeChild(node);

            mark.scrollIntoView({ behavior: "smooth", block: "center" });
            return;
          }
        }
      }
    }
  }
}

/**
 * Clears all highlights from Altvia content
 */
function clearAllHighlights() {
  const marks = document.querySelectorAll(
    ".altvia-content .fp-dark-highlight, .altvia-reference .fp-dark-highlight, .altvia-description .fp-dark-highlight"
  );

  marks.forEach((mark) => {
    const parent = mark.parentNode;
    if (!parent) return;

    // Move the text content back to the parent
    while (mark.firstChild) {
      parent.insertBefore(mark.firstChild, mark);
    }

    // Remove the mark element
    parent.removeChild(mark);

    // Normalize the text nodes
    parent.normalize();
  });
}

/**
 * Public function to clear highlights (can be called from outside)
 */
export function clearAltviaHighlights() {
  clearAllHighlights();
}
